import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { call, createApi, type Api, type Session } from '@edil/api-client';
import type { Admin } from './types';
import { ToastProvider, useToast } from '@edil/ui';

export const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3010';
export type { Admin };
type Tone = 'info' | 'success' | 'error';

interface AppState {
  api: Api;
  admin: Admin | null;
  ready: boolean;
  signIn(token: string, admin: Admin): void;
  signOut(): Promise<void>;
  can(permission: string): boolean;
  toast(text: string, tone?: Tone): void;
}

const Ctx = createContext<AppState | null>(null);
const KEY = 'edil.admin.session';

export function useApp(): AppState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useApp outside AppProvider');
  return v;
}

// Admin sessions live in sessionStorage: they end with the browser tab, and the server also ends
// them after 30 idle minutes (§5.13).
function stored(): string | null {
  try {
    return sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
}
function store(token: string | null) {
  try {
    if (token) sessionStorage.setItem(KEY, token);
    else sessionStorage.removeItem(KEY);
  } catch {
    /* memory only */
  }
}

export function AppProvider({ children }: { children: ReactNode }) {
  return (
    <ToastProvider>
      <State>{children}</State>
    </ToastProvider>
  );
}

function State({ children }: { children: ReactNode }) {
  const push = useToast();
  const token = useRef<string | null>(stored());
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [ready, setReady] = useState(false);

  const setSession = useCallback((s: Session | null) => {
    token.current = s?.accessToken ?? null;
    store(token.current);
    if (!s) setAdmin(null);
  }, []);

  const api = useMemo(
    () =>
      createApi({
        baseUrl: API_URL,
        getSession: () => (token.current ? { accessToken: token.current, refreshToken: '' } : null),
        setSession,
        getLanguage: () => 'en',
        deviceId: 'back-office',
        channel: 'web',
        refresh: false,
      }),
    [setSession],
  );

  useEffect(() => {
    if (!token.current) return setReady(true);
    call(api.GET('/admin/v1/me'))
      .then(setAdmin)
      .catch(() => setSession(null))
      .finally(() => setReady(true));
  }, [api, setSession]);

  const value: AppState = {
    api,
    admin,
    ready,
    signIn: (t, a) => {
      token.current = t;
      store(t);
      setAdmin(a);
    },
    signOut: async () => {
      await call(api.POST('/admin/v1/auth/logout', {})).catch(() => {});
      setSession(null);
    },
    can: (p) => Boolean(admin?.permissions.includes(p)),
    toast: (text, tone = 'info') => push(text, tone),
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
